import { CheckCircleIcon } from "@heroicons/react/20/solid"
import { ArrowRightIcon } from "@heroicons/react/24/outline"
import { useLocation } from "wouter-preact"
import gmailLogo from "/img/integration/gmail.png"
import microsoftLogo from "/img/integration/microsoft.png"
import { Button } from "@/components/ui/button"
import type { EmailSource } from "./email_sources"

interface EmailSourceWallProps {
  sources: EmailSource[]
  /** Where "Connect a mailbox" and every tile go: the remittance-email connect page. */
  href: string
  connected?: boolean
  /** Search results: just the matching tiles under a one-line heading, without the pitch. */
  compact?: boolean
  className?: string
}

function EmailSourceTile({ source, onSelect }: { source: EmailSource; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      title={source.name}
      class="group flex h-[72px] items-center justify-center rounded-lg border border-gray-200/80 bg-white px-3 transition-all hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md">
      {source.logo ? (
        <img
          src={source.logo}
          alt={source.name}
          loading="lazy"
          decoding="async"
          class="max-h-9 max-w-full object-contain opacity-90 transition-opacity group-hover:opacity-100"
        />
      ) : (
        <span class="text-center text-[11px] font-semibold leading-tight text-gray-700">{source.name}</span>
      )}
    </button>
  )
}

/**
 * The customers that pay by emailed remittance, as one wall: a brand connects a single mailbox and
 * every one of these is covered, so they are shown together rather than as a card each.
 */
export function EmailSourceWall({ sources, href, connected, compact, className }: EmailSourceWallProps) {
  const [, setLocation] = useLocation()

  function open_connect() {
    setLocation(href)
  }

  if (sources.length === 0) return null

  if (compact) {
    return (
      <div class={className}>
        <h2 class="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Read from your mailbox</h2>
        <div class="grid grid-cols-4 gap-2">
          {sources.map((source: EmailSource) => (
            <EmailSourceTile key={source.id} source={source} onSelect={open_connect} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <section
      class={`overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-b from-slate-50 via-white to-white ${className ?? ""}`}>
      <div class="px-6 pt-6">
        <div class="flex items-center gap-2">
          <span class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white shadow-sm">
            <img src={gmailLogo} alt="Gmail" class="h-4 w-4 object-contain" />
          </span>
          <span class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white shadow-sm">
            <img src={microsoftLogo} alt="Microsoft 365" class="h-4 w-4 object-contain" />
          </span>
          <span class="ml-1 text-xs font-semibold uppercase tracking-wide text-gray-500">No portal needed</span>
          {connected && (
            <span class="ml-auto inline-flex items-center gap-1 text-xs font-medium text-green-600">
              <CheckCircleIcon class="h-4 w-4" />
              Connected
            </span>
          )}
        </div>
        <h2 class="mt-4 text-xl font-semibold tracking-tight text-gray-900">
          One mailbox. {sources.length} more customers.
        </h2>
        <p class="mt-1.5 text-sm leading-relaxed text-gray-500">
          These customers email a remittance instead of running a portal. Connect the inbox they send it to and every
          payment and deduction is booked the day it arrives, tied out to the cent.
        </p>
      </div>

      <div class="mt-5 grid grid-cols-4 gap-2 px-6">
        {sources.map((source: EmailSource) => (
          <EmailSourceTile key={source.id} source={source} onSelect={open_connect} />
        ))}
      </div>

      <div class="mt-6 flex items-center justify-between gap-4 border-t border-gray-100 bg-gray-50/60 px-6 py-4">
        <p class="text-xs text-gray-500">Paid by someone who isn't here? Forward us one remittance and we'll add them.</p>
        <Button className="shrink-0" onClick={open_connect}>
          {connected ? "Manage mailbox" : "Connect a mailbox"}
          <ArrowRightIcon class="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    </section>
  )
}
